/**
 * Cloud Sample Module Initialization
 * 
 * Utility for initializing and managing the DJI cloud module following the sample implementation.
 * This handles the loading sequence, callback registration, and workspace configuration.
 */

import djiBridge from './cloud-sample-DjiBridge';

// Component names enumeration (matching the sample)
export const EComponentName = {
  Thing: 'thing',        // Cloud/MQTT module
  Api: 'api',            // API connectivity
  Liveshare: 'liveshare', // Livestreaming capability
  Ws: 'ws',              // WebSocket connectivity
  Map: 'map',            // Map capability
  Tsa: 'tsa',            // TSA capability
  Media: 'media',        // Media management
  Mission: 'mission'     // Mission/wayline management
};

// Status values enumeration (matching the sample)
export const EStatusValue = {
  CONNECTED: 'Connected',
  DISCONNECT: 'Disconnected'
};

// Debug information storage
const debugInfo = {
  moduleResponses: {},
  callHistory: []
};

// Add a call to debug history with timestamp
const addToDebugHistory = (category, action, params = null, response = null) => {
  debugInfo.callHistory.push({
    timestamp: new Date().toISOString(),
    category,
    action,
    params: params ? JSON.stringify(params) : null,
    response: response ? JSON.stringify(response) : null
  });
  
  // Keep history limited to 100 items
  if (debugInfo.callHistory.length > 100) {
    debugInfo.callHistory.shift();
  }
  
  // Also log to console for immediate visibility
  console.log(`[CLOUD-SAMPLE][${category}][${action}]`, 
    params ? { params } : '',
    response ? { response } : '');
};

/**
 * Try to load a specific component with the provided parameters
 * This follows the pattern from the DJI sample where components are loaded individually
 * 
 * @param {string} componentName - The name of the component to load 
 * @param {object} params - The parameters to initialize the component with
 * @returns {boolean} - Whether the component was loaded successfully
 */
export const tryLoadModule = (componentName, params) => {
  console.log(`[CLOUD-SAMPLE] Attempting to load component: ${componentName}`);
  addToDebugHistory('module', 'tryLoadModule', { componentName, params });
  
  try {
    // Skip if component is already loaded
    if (isComponentLoaded(componentName)) {
      console.log(`[CLOUD-SAMPLE] Component ${componentName} is already loaded`);
      return true;
    }
    
    // Call the bridge to load the component
    const result = djiBridge.loadComponent(componentName, params);
    console.log(`[CLOUD-SAMPLE] Loading ${componentName} result:`, result);
    
    // Special handling for Thing component
    if (componentName === EComponentName.Thing && result === true && params) {
      // Call thingConnect directly with credentials if available
      if (window.djiBridge && typeof window.djiBridge.thingConnect === 'function') {
        console.log('[CLOUD-SAMPLE] Calling window.djiBridge.thingConnect directly from cloudModule');
        window.djiBridge.thingConnect(params.username, params.password, params.connectCallback || 'connectCallback');
      }
    }
    
    return result === true;
  } catch (error) {
    console.error(`[CLOUD-SAMPLE] Error loading ${componentName}:`, error);
    return false;
  }
};

/**
 * Check if a component is loaded
 * 
 * @param {string} componentName - The name of the component to check
 * @returns {boolean} - Whether the component is loaded
 */
export const isComponentLoaded = (componentName) => {
  try {
    return djiBridge.isComponentLoaded(componentName);
  } catch (error) {
    console.error(`[CLOUD-SAMPLE] Error checking if ${componentName} is loaded:`, error);
    return false;
  }
};

/**
 * Check if the Thing (MQTT) component is connected
 * 
 * @returns {boolean} - Whether the Thing component is connected
 */
export const isThingConnected = () => {
  try {
    return djiBridge.thingGetConnectState();
  } catch (error) {
    console.error('[CLOUD-SAMPLE] Error checking if Thing is connected:', error);
    return false;
  }
};

/**
 * Initialize the cloud module with configuration
 * 
 * @param {Object} config - Configuration with MQTT, workspace, and other settings
 * @returns {boolean} - Success status
 */
export const initCloudModule = (config) => {
  console.log('[CLOUD-SAMPLE] Initializing cloud module with config:', config);
  
  try {
    // Set platform information
    if (config.workspace) {
      djiBridge.platformSetInformation(
        config.workspace.platform_name || config.workspace.platformName,
        config.workspace.workspace_name || config.workspace.workspaceName,
        config.workspace.workspace_desc || config.workspace.workspaceDesc
      );
      
      // Set workspace ID if available
      if (config.workspace.workspace_id || config.workspace.workspaceId) {
        djiBridge.setWorkspaceId(config.workspace.workspace_id || config.workspace.workspaceId);
      }
    }
    
    // Set up API token if available
    if (config.api && config.api.token) {
      djiBridge.setToken(config.api.token);
    }
    
    // Load the Thing (MQTT) component
    if (config.mqtt) {
      const thingParams = {
        host: config.mqtt.host || config.mqtt.brokerUrl,
        username: config.mqtt.username,
        password: config.mqtt.password,
        connectCallback: 'connectCallback'
      };
      
      // Load the Thing component
      return tryLoadModule(EComponentName.Thing, thingParams);
    }
    
    return true;
  } catch (error) {
    console.error('[CLOUD-SAMPLE] Error initializing cloud module:', error);
    return false;
  }
};

/**
 * Refresh the status of all modules
 * 
 * @returns {Object} - Status information for each module
 */
export const refreshStatus = () => {
  const status = {};
  
  // Check status of each module
  Object.values(EComponentName).forEach(component => {
    status[component] = isComponentLoaded(component);
  });
  
  // Add additional connection state for Thing component
  if (status[EComponentName.Thing]) {
    status[`${EComponentName.Thing}Connected`] = isThingConnected();
  }
  
  console.log('[CLOUD-SAMPLE] Module status refresh:', status);
  return status;
};

/**
 * Get debug information
 * 
 * @returns {Object} - Debug information
 */
export const getDebugInfo = () => {
  return { ...debugInfo };
};

// Export default object with all functions
export default {
  EComponentName,
  EStatusValue,
  tryLoadModule,
  isComponentLoaded,
  isThingConnected,
  initCloudModule,
  refreshStatus,
  getDebugInfo
}; 